import React from 'react';

import { UiSvgIcon } from '@/components/ui/SvgIcon';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_CHARACTERISTIC_ICON } from '@/pages/Onboarding/Layouts/Default/screens/AICharacteristic/constants';
import { TOPICS_OPTIONS } from '@/pages/Onboarding/Layouts/Default/screens/Topics/constants';
import { onboardingInterestsOptions } from '@/pages/Onboarding/Layouts/screens/Interests/constants';

import { ImageWithDataStyles } from './ImageWithData.styles';
import { getCategoriesForView } from './utils/getCategoriesForView';
import { getInterestsForView } from './utils/getInterestsForView';
import { getMainImageSrc } from './utils/getMainImageSrc';

export const PaywallSectionImageWithData: React.FC = () => {
  const { currentUser, user } = useAccount();

  const aiCharacteristic =
    user?.onboarding_data?.ai_characteristic || 'Neutral';
  const seekingForObj =
    TOPICS_OPTIONS.find((opt) => opt.id === user?.onboarding_data?.topics) ||
    TOPICS_OPTIONS[0];

  const categories = getCategoriesForView(currentUser.selectedCategories);

  const interests = getInterestsForView(user?.onboarding_data?.interests);

  return (
    <ImageWithDataStyles>
      <OnboardingTitle className="image-with-data-title">
        {currentUser.name}, here's{' '}
        <UiTypography variant="inherit" color="gradient" component="span">
          your personal
        </UiTypography>{' '}
        plan:
      </OnboardingTitle>
      <div className="image-with-data-container">
        <div className="image-with-data-container-img-wrapper">
          <img src={getMainImageSrc()} alt="AI" />
        </div>
        <div className="image-with-data-user-info">
          <div className="image-with-data-user-info__content">
            <UiTypography
              variant="label-medium"
              className="image-with-data-user-info__content-title"
            >
              Your want to chat with:
            </UiTypography>
            <div className="image-with-data-user-info__content-values">
              {categories.toDisplay.map((category) => (
                <UiTypography
                  key={category.id}
                  className="image-with-data-user-info__content-value"
                  variant="label-small"
                >
                  <img src={category.picture_url} alt="picture_url" />
                  {category.title}
                </UiTypography>
              ))}
              {categories.notDisplayed.length > 0 && (
                <UiTypography
                  variant="label-small"
                  className="image-with-data-user-info__content-value"
                >
                  +{categories.notDisplayed.length} more
                </UiTypography>
              )}
            </div>

            <UiTypography
              variant="label-medium"
              className="image-with-data-user-info__content-title"
            >
              Who are:
            </UiTypography>
            <div className="image-with-data-user-info__content-values">
              <UiTypography
                className="image-with-data-user-info__content-value"
                variant="label-small"
              >
                {AI_CHARACTERISTIC_ICON[aiCharacteristic]} {aiCharacteristic}
              </UiTypography>
            </div>

            <UiTypography
              variant="label-medium"
              className="image-with-data-user-info__content-title"
            >
              You're seeking for:
            </UiTypography>
            <div className="image-with-data-user-info__content-values">
              <UiTypography
                className="image-with-data-user-info__content-value"
                variant="label-small"
              >
                {seekingForObj.title}
              </UiTypography>
            </div>

            <UiTypography
              variant="label-medium"
              className="image-with-data-user-info__content-title"
            >
              You like:
            </UiTypography>
            <div className="image-with-data-user-info__content-values">
              {interests.toDisplay.map((interest) => {
                const iconData = onboardingInterestsOptions.find(
                  (option) =>
                    option.id === interest || option.title === interest,
                );

                if (!iconData) {
                  return null;
                }

                return (
                  <UiTypography
                    className="image-with-data-user-info__content-value"
                    variant="label-small"
                    key={interest}
                  >
                    <UiSvgIcon symbol={iconData.id} />
                    {iconData.title}
                  </UiTypography>
                );
              })}
              {interests.notDisplayed.length > 0 && (
                <UiTypography
                  variant="label-small"
                  className="image-with-data-user-info__content-value"
                >
                  +{interests.notDisplayed.length} more
                </UiTypography>
              )}
            </div>
          </div>
        </div>
      </div>
    </ImageWithDataStyles>
  );
};
